import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Loader, StatusBadge, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { UserPlus, Check, Target } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const ROLE_LABELS = { tac: "TAC", validator: "Validator", am: "AM", pd: "PD Officer", admin: "Admin" };
const ROLES = ["tac", "validator", "am", "pd", "admin"];

export default function UserManagement() {
  const [users, setUsers] = useState(null);
  const [tacs, setTacs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "validator", org: "EnterpriseSG" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => {
    load();
    api.get("/users/tacs").then((r) => setTacs(r.data)).catch(() => setTacs([]));
  }, []);
  if (!users) return <Loader label="Loading users" />;

  const create = async () => {
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "validator", org: "EnterpriseSG" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const update = async (id, patch) => {
    try { await api.patch(`/users/${id}`, patch); toast.success("Updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const approve = async (id) => {
    try { await api.post(`/users/${id}/approve`); toast.success("User approved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        overline="Administration"
        title="User Management"
        description="Approve self-registered partners, create internal accounts, change roles, suspend or revoke access."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-md" data-testid="create-user-button"><UserPlus className="h-4 w-4 mr-1.5" aria-hidden="true" /> Create user</Button>
            </DialogTrigger>
            <DialogContent className="rounded-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Create internal account</DialogTitle>
                <DialogDescription>Directly create an active account for internal roles.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-md" data-testid="user-name-input" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-md" data-testid="user-email-input" /></div>
                <div><Label>Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-md" data-testid="user-password-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="mt-1 rounded-md" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Organisation</Label><Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} className="mt-1 rounded-md" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
                <Button onClick={create} disabled={!form.name || !form.email || !form.password} className="rounded-md" data-testid="confirm-create-user">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u, i) => (
              <motion.tr key={u.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }} className="border-b border-border/60" data-testid={`user-row-${u.id}`}>
                <TableCell className="py-2.5 font-medium text-sm">{u.name}<div className="text-xs text-muted-foreground">{u.org}</div></TableCell>
                <TableCell className="py-2.5 text-sm font-mono">{u.email}</TableCell>
                <TableCell className="py-2.5">
                  <Select value={u.role} onValueChange={(v) => update(u.id, { role: v })}>
                    <SelectTrigger className="h-8 w-[130px] rounded-md text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="py-2.5"><StatusBadge status={u.status} /></TableCell>
                <TableCell className="py-2.5">
                  <div className="flex items-center gap-1.5">
                    {u.status === "pending" && <Button size="sm" className="h-8 rounded-md" onClick={() => approve(u.id)} data-testid={`approve-${u.id}`}><Check className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Approve</Button>}
                    {u.status === "active" && <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => update(u.id, { status: "suspended" })} data-testid={`suspend-${u.id}`}>Suspend</Button>}
                    {(u.status === "suspended" || u.status === "revoked") && <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => update(u.id, { status: "active" })} data-testid={`reactivate-${u.id}`}>Reactivate</Button>}
                    {u.status !== "revoked" && u.role !== "admin" && <Button size="sm" variant="ghost" className="h-8 rounded-md text-destructive hover:text-destructive" onClick={() => update(u.id, { status: "revoked" })} data-testid={`revoke-${u.id}`}>Revoke</Button>}
                    {["validator", "am", "pd"].includes(u.role) && <ScopeDialog user={u} update={update} tacs={tacs} />}
                  </div>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ScopeDialog({ user, update, tacs }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(user.scoped_tac_ids || []);
  useEffect(() => { if (open) setSel(user.scoped_tac_ids || []); }, [open, user.scoped_tac_ids]);
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => { await update(user.id, { scoped_tac_ids: sel }); setOpen(false); };
  const count = (user.scoped_tac_ids || []).length;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-8 rounded-md" data-testid={`scope-${user.id}`}>
          <Target className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> {count ? `${count} scoped` : "All partners"}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Partner scope — {user.name}</DialogTitle>
          <DialogDescription>Select the partners this {user.role.toUpperCase()} can see and act on. Leave empty to grant access to all partners.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-1 py-2">
          {tacs.length === 0 && <div className="text-sm text-muted-foreground">No active partners yet.</div>}
          {tacs.map((t) => (
            <label key={t.id} className="flex items-center gap-3 rounded-md border border-border/60 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`scope-tac-${t.id}`}>
              <Checkbox checked={sel.includes(t.id)} onCheckedChange={() => toggle(t.id)} />
              <span className="text-sm">{t.name}<span className="text-xs text-muted-foreground ml-1 font-mono">{t.org}</span></span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-md" onClick={() => setSel([])} data-testid={`scope-clear-${user.id}`}>Grant all</Button>
          <Button className="rounded-md" onClick={save} data-testid={`save-scope-${user.id}`}>Save scope</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
